/**
 * OrejaSessionHistory - ChatGPT/Claude-style session history sidebar.
 *
 * Groups sessions by date (Today, Yesterday, This Week, Earlier) and displays
 * each session with task counts, timestamps, and phase badges. Supports
 * selecting a session and a loading state.
 */

import { useMemo } from 'react';
import {
  isToday,
  isYesterday,
  isThisWeek,
  formatDistanceToNow,
  format,
  parseISO,
} from 'date-fns';
import { X, Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { OrejaProposals, OrejaCommittedChange } from '@/lib/oreja/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrejaSessionHistoryEntry {
  id: string;
  client_id: string | null;
  /** Resolved client/project name for display. */
  client_name: string | null;
  phase: string;
  created_at: string;
  proposals: OrejaProposals | null;
  committed_changes: OrejaCommittedChange[] | null;
}

export interface OrejaSessionHistoryProps {
  /** List of sessions to display. */
  sessions: OrejaSessionHistoryEntry[];
  /** ID of the currently active session, if any. */
  activeSessionId: string | null;
  /** Callback when a session is selected. */
  onSelectSession: (sessionId: string) => void;
  /** Callback to close the history sidebar. */
  onClose: () => void;
  /** Whether session data is loading. */
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type DateGroup = 'Today' | 'Yesterday' | 'This Week' | 'Earlier';

/**
 * Determines which date group a given ISO timestamp belongs to.
 *
 * @param isoDate - ISO 8601 date string
 * @returns The date group label
 */
function getDateGroup(isoDate: string): DateGroup {
  const date = parseISO(isoDate);
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  if (isThisWeek(date, { weekStartsOn: 1 })) return 'This Week';
  return 'Earlier';
}

/** Ordered list of date groups for consistent rendering. */
const GROUP_ORDER: DateGroup[] = ['Today', 'Yesterday', 'This Week', 'Earlier'];

/**
 * Groups sessions by date bucket and returns them in chronological group order.
 *
 * @param sessions - Array of session entries
 * @returns Array of [groupLabel, sessions[]] tuples
 */
function groupByDate(
  sessions: OrejaSessionHistoryEntry[],
): [DateGroup, OrejaSessionHistoryEntry[]][] {
  const groups = new Map<DateGroup, OrejaSessionHistoryEntry[]>();

  for (const session of sessions) {
    const group = getDateGroup(session.created_at);
    const list = groups.get(group) ?? [];
    list.push(session);
    groups.set(group, list);
  }

  return GROUP_ORDER.filter((g) => groups.has(g)).map((g) => [
    g,
    groups.get(g)!,
  ]);
}

/**
 * Computes a task count label from proposals or committed changes.
 *
 * @param session - The session entry
 * @returns Human-readable count string (e.g. "3 tasks") or null
 */
function getTaskCount(session: OrejaSessionHistoryEntry): string | null {
  if (session.committed_changes?.length) {
    const n = session.committed_changes.length;
    return `${n} task${n !== 1 ? 's' : ''}`;
  }
  if (session.proposals) {
    const n =
      (session.proposals.updated_tasks?.length ?? 0) +
      (session.proposals.new_tasks?.length ?? 0);
    if (n > 0) return `${n} task${n !== 1 ? 's' : ''}`;
  }
  return null;
}

/**
 * Returns a Badge variant and label for a session phase.
 *
 * @param phase - The session phase string
 * @returns Object with variant and label for the Badge component
 */
function phaseBadgeProps(phase: string): {
  variant: 'healthy' | 'attention' | 'outline';
  label: string;
} {
  switch (phase) {
    case 'committed':
      return { variant: 'healthy', label: 'Done' };
    case 'reviewing':
    case 'refining':
      return { variant: 'attention', label: 'Review' };
    case 'clarifying':
      return { variant: 'attention', label: 'Questions' };
    default:
      return { variant: 'outline', label: phase };
  }
}

/**
 * Returns a human-readable display name for a session.
 * Prefers client_name, falls back to truncated session ID.
 *
 * @param session - The session entry
 * @returns Display name string
 */
function getSessionDisplayName(session: OrejaSessionHistoryEntry): string {
  if (session.client_name) return session.client_name;
  return `Session ${session.id.slice(0, 6)}`;
}

/**
 * Formats a session timestamp for display.
 *
 * @param isoDate - ISO 8601 date string
 * @returns Relative time string (e.g. "2h ago") or formatted time (e.g. "3:45 PM")
 */
function formatTimestamp(isoDate: string): string {
  const date = parseISO(isoDate);
  if (isToday(date)) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  return format(date, 'h:mm a');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Session history sidebar for browsing past Oreja sessions.
 *
 * @param props - Component props
 * @returns Sidebar panel with grouped session list
 */
export function OrejaSessionHistory({
  sessions,
  activeSessionId,
  onSelectSession,
  onClose,
  isLoading = false,
}: OrejaSessionHistoryProps) {
  const grouped = useMemo(() => groupByDate(sessions), [sessions]);

  return (
    <div className="flex h-full flex-col border-r bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h2 className="text-sm font-semibold">History</h2>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onClose}
          aria-label="Close history"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <ScrollArea className="flex-1">
          <div className="space-y-4 p-3">
            {grouped.map(([group, groupSessions]) => (
              <div key={group}>
                <p className="mb-1.5 px-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {group}
                </p>
                <div className="space-y-1">
                  {groupSessions.map((session) => {
                    const isActive = session.id === activeSessionId;
                    const taskCount = getTaskCount(session);
                    const badge = phaseBadgeProps(session.phase);

                    return (
                      <button
                        key={session.id}
                        type="button"
                        onClick={() => onSelectSession(session.id)}
                        className={cn(
                          'w-full rounded-md px-3 py-2 text-left text-sm transition-colors',
                          'hover:bg-accent',
                          isActive && 'ring-2 ring-primary bg-accent',
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate font-medium">
                            {getSessionDisplayName(session)}
                          </span>
                          <Badge variant={badge.variant} className="shrink-0 text-[10px] px-1.5">
                            {badge.label}
                          </Badge>
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                          {taskCount && <span>{taskCount}</span>}
                          <span>{formatTimestamp(session.created_at)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {grouped.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No sessions yet
              </p>
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
